/**
 * 열린 popup을 앵커에 붙여 두는 재배치 계층. scroll/resize와 앵커·경계의 크기 변화를 rAF로 프레임당
 * 한 번으로 합쳐 `updatePosition`을 부른다. 앵커 이탈로 닫는 판정은 `useAnchorHiddenClose`가 따로 맡는다.
 */
import { useEffect } from "react";
import { collectClipAncestors } from "./anchorVisibility";

export function useAnchoredReposition({
  open,
  anchorEl,
  matchAnchorWidth,
  boundary,
  observeClippingAncestors = false,
  panel = null,
  repositionOnScroll,
  repositionOnResize,
  updatePosition,
}: {
  open: boolean;
  /** rect 앵커의 element. 폭 맞춤 또는 clipping 경계 추적 시 크기 변화를 관찰한다. */
  anchorEl: HTMLElement | null;
  matchAnchorWidth: boolean;
  boundary: HTMLElement | null | undefined;
  observeClippingAncestors?: boolean;
  /** 경계 안에 배치하는 panel의 콘텐츠 크기 변화를 추적한다. */
  panel?: HTMLElement | null;
  repositionOnScroll: boolean;
  repositionOnResize: boolean;
  updatePosition: () => void;
}) {
  useEffect(() => {
    const anchorElement =
      (matchAnchorWidth || observeClippingAncestors) && anchorEl?.isConnected ? anchorEl : null;
    if (
      !open ||
      (!repositionOnScroll && !repositionOnResize && !anchorElement && !boundary && !panel)
    ) {
      return;
    }

    // capture scroll은 모달 Dialog 내부 등 모든 스크롤 컨테이너에서 fire한다 → 매 fire 동기 updatePosition
    // (getBoundingClientRect ×2 + setState)은 콘텐츠 많은 화면에서 jank. rAF로 프레임당 1회로 coalesce하고
    // passive로 등록한다(handler가 preventDefault 안 하므로 안전).
    let raf = 0;
    const schedule = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        updatePosition();
      });
    };

    if (repositionOnResize) {
      window.addEventListener("resize", schedule, { passive: true });
    }
    if (repositionOnScroll) {
      window.addEventListener("scroll", schedule, { passive: true, capture: true });
    }
    const resizeObserver =
      (anchorElement || boundary || panel) && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(schedule)
        : null;
    if (anchorElement) resizeObserver?.observe(anchorElement, { box: "border-box" });
    if (anchorElement && observeClippingAncestors) {
      for (const { element } of collectClipAncestors(anchorElement))
        resizeObserver?.observe(element);
    }
    if (boundary) resizeObserver?.observe(boundary, { box: "border-box" });
    if (panel) resizeObserver?.observe(panel);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", schedule);
      window.removeEventListener("scroll", schedule, true);
    };
  }, [
    anchorEl,
    boundary,
    matchAnchorWidth,
    observeClippingAncestors,
    panel,
    open,
    repositionOnResize,
    repositionOnScroll,
    updatePosition,
  ]);
}
